// DailyLogs.jsx
import React from "react";
import "../../../../../Stylesheets/Dashboard/Tabs/DailyLogs.css";

import { useDispatch, useSelector } from "react-redux";
import {
  fetchDailyLogs,
  createDailyLog,
  selectDailyLogs,
  selectDailyLogsMeta,
  selectDailyLogsStatus,
  selectCreateLogStatus,
  deleteDailyLog, selectDeletingMap, resetDailyLogs
} from "../../../../../Redux/Projects/dailyLogSlice"; // <-- check path

import { getAuth } from "firebase/auth";
import { storage } from "../../../../../firebase"; // <-- you export storage already
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";


import Spinner from '../../../../../Components/Common/Spinner'

const DailyLogs = ({ currentProjectId }) => {


  // inside DailyLogs component
  const projectId = currentProjectId;

  console.log(projectId)
  const dispatch = useDispatch();
  const list = useSelector(selectDailyLogs);
  const meta = useSelector(selectDailyLogsMeta);
  const status = useSelector(selectDailyLogsStatus);
  const creating = useSelector(selectCreateLogStatus);
  const deletingMap = useSelector(selectDeletingMap);

  // ------- Controls -------
  const [layout, setLayout] = React.useState("gallery"); // 'gallery' | 'timeline'
  const [sort, setSort] = React.useState("desc");
  const [q, setQ] = React.useState("");
  const [onlyImg, setOnlyImg] = React.useState(false);

  // ------- Form -------
  const [form, setForm] = React.useState({
    date: "",
    title: "",
    notes: "",
    file: null,
    preview: "",
  });
  const [uploading, setUploading] = React.useState(false);
  const [uploadPct, setUploadPct] = React.useState(0);

  // Fetch on mount / project change
  React.useEffect(() => {
    if (!projectId) return;

    // clear previous project’s logs
    dispatch(resetDailyLogs());

    // then fetch for current project
    dispatch(
      fetchDailyLogs({
        projectId,
        params: { sort: "new", page: 1, limit: 20 },
      })
    );
  }, [dispatch, projectId]);

  // Local image pick + preview
  const onPickImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setForm((p) => ({ ...p, file: f }));

    const r = new FileReader();
    r.onload = () => setForm((p) => ({ ...p, preview: r.result }));
    r.readAsDataURL(f);
  };

  // Firebase upload helper (component-side)
  const uploadToFirebase = (file) =>
    new Promise((resolve, reject) => {
      if (!file) return resolve(null);
      const safeName = (file.name || "photo").replace(/\s+/g, "_");
      const path = `dailyLogs/${projectId}/${Date.now()}_${safeName}`;
      const storageRef = ref(storage, path);
      const task = uploadBytesResumable(storageRef, file);

      setUploading(true);
      setUploadPct(0);

      task.on(
        "state_changed",
        (snap) => {
          const pct = Math.round(
            (snap.bytesTransferred / snap.totalBytes) * 100
          );
          setUploadPct(pct);
        },
        (err) => {
          setUploading(false);
          reject(err);
        },
        async () => {
          setUploading(false);
          const url = await getDownloadURL(task.snapshot.ref);
          resolve(url);
        }
      );
    });

  // Create log
  const handleAdd = async () => {
    try {
      if (!projectId) throw new Error("Missing projectId");
      if (!form.title && !form.notes)
        throw new Error("Please add a title or notes");

      // 1) upload (if any)
      const imageUrl = form.file ? await uploadToFirebase(form.file) : null;

      // 2) dispatch with imageUrl
      const auth = getAuth();
      await dispatch(
        createDailyLog({
          projectId,
          type: "material_receive", // or any selected type
          message: form.title || form.notes || "New daily log",
          metadata: form.notes ? { notes: form.notes } : undefined,
          imageUrl, // <<-- URL from Firebase
          logDate: form.date || undefined,
        })
      ).unwrap();

      // 3) reset form
      setForm({ date: "", title: "", notes: "", file: null, preview: "" });
      setUploadPct(0);
    } catch (e) {
      console.error(e);
      // show toast if you use one
    }
  };

  // ---------- Transform backend logs to UI-friendly shape ----------
  // Your backend returns: { _id, createdAt, message, imageUrl, type, metadata }
  const uiLogs = React.useMemo(() => {
    const base = Array.isArray(list) ? list : [];
    let mapped = base.map((x) => ({
      id: x._id,
      date: x.createdAt || x.date,
      title: x.message,
      desc:
        (x.metadata && (x.metadata.notes || x.metadata.desc)) ||
        "", // show notes if present
      img: x.imageUrl || "",
      tags: x.type ? [x.type] : [],
      notes: x.metadata?.notes || "",
    }));

    // client-side search/filter/sort like before
    if (q.trim()) {
      const s = q.toLowerCase();
      mapped = mapped.filter(
        (x) =>
          x.title?.toLowerCase().includes(s) ||
          x.desc?.toLowerCase().includes(s) ||
          x.tags?.join(" ").toLowerCase().includes(s)
      );
    }
    if (onlyImg) mapped = mapped.filter((x) => !!x.img);
    mapped.sort((a, b) =>
      sort === "asc"
        ? new Date(a.date) - new Date(b.date)
        : new Date(b.date) - new Date(a.date)
    );
    return mapped;
  }, [list, q, onlyImg, sort]);

  const grouped = React.useMemo(() => {
    const g = {};
    uiLogs.forEach((x) => {
      const key = new Date(x.date).toLocaleString("en-GB", {
        month: "long",
        year: "numeric",
      });
      g[key] = g[key] ? [...g[key], x] : [x];
    });
    return g;
  }, [uiLogs]);

  const onDelete = async (id) => {
    // optional confirm:
    if (!window.confirm('Delete this log?')) return;
    try {
      await dispatch(deleteDailyLog(id)).unwrap();
    } catch (e) {
      console.error('[DailyLogs] delete failed:', e);
    }
  };

  return (
    <div className="dlx-wrap">
      {/* LEFT: Quick Add */}
      <aside className="dlx-form">
        <header className="dlx-form-head">
          <h2>Daily Log</h2>
          <span className="pill">Quick Add</span>
        </header>

        <div className="f-field">
          <label>Date</label>
          <input
            type="date"
            className="f-input"
            value={form.date}
            onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
          />
        </div>
        <div className="f-field">
          <label>Title</label>
          <input
            type="text"
            className="f-input"
            placeholder="Short, punchy title"
            value={form.title}
            onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
          />
        </div>
        <div className="f-field">
          <label>Notes</label>
          <textarea
            className="f-input"
            rows={3}
            placeholder="Optional note"
            value={form.notes}
            onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
          />
        </div>

        <div className="f-upload">
          <label className="f-upload-btn">
            <input type="file" accept="image/*" onChange={onPickImage} />
            Upload photo
          </label>
          {form.preview ? (
            <div className="f-thumb">
              <img src={form.preview} alt="preview" />
            </div>
          ) : (
            <div className="f-drop">Optional</div>
          )}
        </div>

        {uploading && (
          <div style={{ fontSize: 12, marginTop: 6 }}>Uploading… {uploadPct}%</div>
        )}

        <div className="f-actions">
          <button className="btn ghost" type="button">
            Save draft
          </button>
          <button
            className="btn primary"
            type="button"
            onClick={handleAdd}
            disabled={creating === "loading" || uploading}
          >
            {creating === "loading" || uploading ? (
              <>
                <Spinner inline size={16} label="Saving…" />
                Saving…
              </>
            ) : (
              "Add"
            )}
          </button>
        </div>
      </aside>

      {/* RIGHT: Visual List */}
      <section className="dlx-board">
        {status === "loading" && (
          <div className="loader-overlay">
            <Spinner size={56} label="Loading logs…" />
          </div>
        )}
        <div className="dlx-toolbar">
          <div className="search">
            <span>🔎</span>
            <input
              placeholder="Search title, tags…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>

          <div className="tools">
            <button
              className={`chip ${onlyImg ? "on" : ""}`}
              onClick={() => setOnlyImg((v) => !v)}
            >
              📷 With image
            </button>

            <select value={sort} onChange={(e) => setSort(e.target.value)}>
              <option value="desc">Newest first</option>
              <option value="asc">Oldest first</option>
            </select>

            <div className="view-toggle">
              <button
                className={`vt-btn ${layout === "gallery" ? "active" : ""}`}
                onClick={() => setLayout("gallery")}
                title="Gallery"
              >
                ▦
              </button>
              <button
                className={`vt-btn ${layout === "timeline" ? "active" : ""}`}
                onClick={() => setLayout("timeline")}
                title="Timeline"
              >
                ☰
              </button>
            </div>
          </div>
        </div>

        {layout === "gallery" ? (
          <div className="grid">
            {uiLogs.map((x) => {
              const d = new Date(x.date).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              });
              return (
                <article className="card" key={x.id}>
                  <div className="card-media">
                    {x.img ? (
                      <img src={x.img} alt={x.title} />
                    ) : (
                      <div className="noimg">{d}</div>
                    )}
                    <div className="overlay">
                      <span className="date">{d}</span>
                      <h3 className="title">{x.title}</h3>
                    </div>
                    <button
                      className="kill"
                      aria-label="Delete log"
                      onClick={() => onDelete(x.id)}
                      title="Delete"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="card-body">
                    <p className="desc clamp-2">{x.desc}</p>
                    <div className="tags">
                      {x.tags?.map((t) => (
                        <span key={t}>#{t}</span>
                      ))}
                    </div>
                    {x.notes && <div className="note clamp-1">⚑ {x.notes}</div>}
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="timeline">
            {Object.keys(grouped).map((m) => (
              <div className="t-group" key={m}>
                <div className="t-head">
                  <span className="dot" />
                  <h4>{m}</h4>
                </div>
                {grouped[m].map((x) => {
                  const d = new Date(x.date).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  });
                  return (
                    <div className="t-item" key={x.id}>
                      <div className="t-date">{d}</div>
                      <div className="t-main">
                        <div className="t-title">{x.title}</div>
                        <p className="t-desc clamp-2">{x.desc}</p>
                        <div className="t-meta">
                          {x.tags?.map((t) => (
                            <span key={t}>#{t}</span>
                          ))}
                          {x.notes && <em> • {x.notes}</em>}
                        </div>
                      </div>

                      <div className="t-thumb">
                        {x.img ? (
                          <img src={x.img} alt="thumb" />
                        ) : (
                          <div className="noimg sm">No image</div>
                        )}
                        <button
                          className="kill inline"
                          aria-label="Delete log"
                          onClick={() => onDelete(x.id)}
                          title={deletingMap[x.id] === 'loading' ? 'Deleting…' : 'Delete'}
                          disabled={deletingMap[x.id] === 'loading'}   // ✅
                        >
                          {deletingMap[x.id] === 'loading' ? <Spinner inline size={14} label="Deleting…" /> : '✕'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default DailyLogs;
